import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedRows, setSelectedItem, setSelectAll, setDataList } from '../../components/PerinHealthGridSlice';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import FilterManagementView from '../../components/FilterManagementView';
import { closeModal } from '../../components/PerinHealthModelSlice';
import managementStyles from '../ManagementPages.module.css';
import { setRoleInfo, setEHRAccountState, resetRegistrationState, setUpdatingState } from '../../components/AccountManagement/RegistrationSlice';
import useAxios from '../../services/AxiosConfig';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { resetDirty } from '../../slicers/DirtyCheckSlice';
import PerinHealthModal from '../../components/PerinHealthModel';

const EhrAccounts = () => {
    const user = useSelector((state) => state.userInfo);

    const navigate = useNavigate();
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    let selectedRows = perinHealthGrid.selectedRows;
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const mirthAxios = useAxios("https://mirth.perinhealth.com/api/");
    const orgId = user.groups[0]?.organizationId;

    const [roles, setRoles] = useState([]);
    
    useEffect(() => {
        axios.get(`role/list/${user.id}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                setRoles(responseData.data.data);
            } else {
                console.error('Invalid data format:');
            }
        })
        .catch((err) => {
            //console.log(err);
        });
    }, []);

    const headers = [
        { key: 'patient_id', label: "ID", sortable: true },
        { key: 'mrn', label: "MRN", sortable: true },
        { key: 'official_name', label: "Name", sortable: true },
        { key: 'phone', label: "Phone", sortable: true },
        { key: 'email', label: "Email", sortable: true },
        { key: 'perin_user_id', label: "User ID", sortable: true },
    ];

    useEffect(() => {
        dispatch(setSelectedItem({}));
        dispatch(resetRegistrationState());
        getUsers();
    }, []);

    const handleDialogButtonClick = () => {
        dispatch(resetDirty());
        dispatch(closeModal());
    };
    
    const getUsers = async () => {
        try {
            if (user.isAdmin) {
                // Fetch data for all organizations in parallel
                const organizationPromises = user.groups.map(async (group) => {
                    const [patientsRes, practitionersRes] = await Promise.all([
                        mirthAxios.get(`${group.organizationId}/patients`),
                        mirthAxios.get(`${group.organizationId}/practitioners`)
                    ]);
                    
                    const patients = Array.isArray(patientsRes.data.data) ? patientsRes.data.data.map((item) => ({ ...item, organizationId: group.organizationId })) : [];
                    const practitioners = Array.isArray(practitionersRes.data.data) ? practitionersRes.data.data.map((item) => ({ ...item, organizationId: group.organizationId })) : [];
                    
                    return [...patients, ...practitioners].filter(user => user.perin_org_id === group.organizationId);
                });
                
                const allUsersArrays = await Promise.all(organizationPromises);
                const allUsers = allUsersArrays.flat();
                const combined = formatUsers(allUsers);
                dispatch(setDataList(combined));
            } else {
                const [patientsRes, practitionersRes] = await Promise.all([
                    mirthAxios.get(`${orgId}/patients`),
                    mirthAxios.get(`${orgId}/practitioners`)
                ]);
                
                const patients = Array.isArray(patientsRes.data.data) ? patientsRes.data.data : [];
                const practitioners = Array.isArray(practitionersRes.data.data) ? practitionersRes.data.data : [];

                const combined = formatUsers([...patients, ...practitioners]);

                const filteredCombined = combined.filter(user => user.perin_org_id === orgId);


                dispatch(setDataList(filteredCombined));
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Failed to fetch EHR accounts",
                    type: 'danger',
                })
            );
        }
    };

    const onRowClickHandler = (rowItem) => {
        const id = rowItem["perin_user_id"];
        const role = rowItem["role"];

        const roleInfo = role === 'Practitioner'
            ? "Doctor"
            : "Patient";

        const roleInformation = roles.find(role => role.category === roleInfo);

        if (!roleInformation) {
           dispatch(
            showAlert({
                header: "Error",
                message: "Role not found for this user and group",
                type: 'danger',
            })
           );
           return;
        }

        const formattedRoleInfo = {
            id: roleInformation.id,
            name: roleInformation.name,
            category: roleInformation.category,
            organizationId: roleInformation.organizationId,
        };

        if (id == null) {
            dispatch(setEHRAccountState(rowItem));
            dispatch(setRoleInfo(formattedRoleInfo));
            navigate(`/create-account?ehr=true`);
        } else {
            axios.get('user/' + id)
                .then((responseData) => {
                    if (responseData.data.data) {
                        dispatch(setUpdatingState(responseData.data.data));
                        navigate(`/create-account?ehr=true`);
                    } else {
                        console.error('error');
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    };


    const parseName = (fullName) => {
        if (!fullName || !fullName.includes(',')) {
            return { firstName: '', lastName: fullName };
        }

        const [lastNamePart, rest] = fullName.split(',', 2);
        const trimmedRest = rest.trim();

        // Optionally limit to first word or first two words
        const firstName = trimmedRest.split(' ').slice(0, 2).join(' ');

        return {
            firstName,
            lastName: lastNamePart.trim()
        };
    };

    const formatUsers = (data) => {
        const userIdSet = new Set();
        let newFormattedUserList = [];

        if (data.length >= 1) {
            newFormattedUserList = data.reduce((acc, user) => {
                const isPatient = user.role === 'Patient';
                const userId = isPatient ? user.patient_id : user.practitioner_id;

                if (!userIdSet.has(userId)) {
                    userIdSet.add(userId);

                    if (isPatient) {
                        const genderCode = user.gender === 'female'
                            ? 13
                            : user.gender === 'male'
                                ? 14
                                : 12;

                        acc.push({
                            ...user,
                            email: user.email,
                            phone: user.phone,
                            fullName: user.official_name,
                            profilePictureURL: user.profilePictureURL,
                            id: user.patient_id,
                            countryCode: "+1",
                            address: {
                                type: 397,
                                address1: user.address_line,
                                city: user.city,
                                state: user.state,
                                zip: user.postal_code,
                                country: user.country,
                            },
                            healthInfo: {
                                gender: genderCode,
                                dateOfBirth: user.birth_date
                            }
                        });
                    } else if (user.role === 'Practitioner') {
                        const { firstName, lastName } = parseName(user.name);
                        acc.push({
                            ...user,
                            email: user.email,
                            phone: user.phone_number,
                            fullName: user.name,
                            profilePictureURL: user.profilePictureURL,
                            id: user.practitioner_id,
                            countryCode: "+1",
                            mrn: user.npi,
                            official_name: user.name,
                            patient_id: user.practitioner_id,
                            firstName,
                            lastName,
                            address: {
                                type: 397,
                                address1: user.address_line,
                                city: user.city,
                                state: user.state,
                                zip: user.postal_code,
                                country: user.country,
                            },
                            healthInfo: {
                                gender: user.gender === 'female'
                                    ? 13
                                    : user.gender === 'male'
                                        ? 14
                                        : 12,
                                dateOfBirth: null // practitioners may not have birth_date
                            }
                        });
                    }
                }

                return acc;
            }, []);
        }
        return newFormattedUserList;
    };

    const onAddHandler = (selectedRole, roleId, category) => {
        if (selectedRole) {
            dispatch(setRoleInfo({ "id": roleId, "name": selectedRole, "category": category }));
            navigate(`/create-account?ehr=true`);
        } else {
            return;
        }
    };

    const onDeleteHandler = () => {
        const deletePromises = selectedRows.map((user) => {
            const id = user.id;
            return axios.delete(`user/${id}`);
        });

        Promise.all(deletePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getUsers();
                dispatch(closeModal());
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Successfully deleted account`,
                        type: 'success',
                    })
                );
            })
            .catch((errors) => {
                dispatch(closeModal());
                const errorMessage = errors.response.data.message;
                dispatch(
                    showAlert({
                        header: "Error",
                        message: `Could not delete accounts: ${errorMessage}`,
                        type: 'danger',
                    })
                );
                console.error('Error deleting rows:', errors);
            });
    };

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={user.isAdmin}
                addButtonLabel='Create Accounts'
                onAddHandler={onAddHandler}
                onDeleteHandler={onDeleteHandler}
                modelHeader='Delete Account?'
                showAddButton={false}
                showDeleteButton={false}
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={perinHealthGrid.dataList}
                    headers={headers}
                    showCheckbox={false}
                    onRowValueClick={onRowClickHandler}
                    rowValueAction={'patient_id'}
                    onDeleteRow={onDeleteHandler}
                    accounts={true}
                    showHeaderFilter={true}
                    showProfilePicture="fullName"
                    gridHeight='100%'
                    useChips={true}
                />
            </div>
            <PerinHealthModal
                modalId={"CreateAccount"}
                handleButtonClick={handleDialogButtonClick}
            />
        </div>
    )
}

export default EhrAccounts;
